import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getConteudoChave } from '@/api/adminApi'
import { readConteudoSnapshot, subscribeConteudoUpdated } from '@/utils/conteudoSync'

/** Valor guardado no conteúdo: '1' = ativo, '0' = desativado; null/omitido = ativo. */
export function isConteudoFlagEnabled(raw: string | null | undefined): boolean {
  return (raw ?? '1') !== '0'
}

/** Só activa se a chave existir no objecto (evita mostrar tudo no refresh antes do GET). */
export function isConteudoFlagInData(
  data: Record<string, string | null>,
  key: string
): boolean {
  if (!(key in data)) return false
  return isConteudoFlagEnabled(data[key])
}

function enabledFromSnapshot(chave: string): boolean | null {
  const snap = readConteudoSnapshot()
  if (!(chave in snap)) return null
  return isConteudoFlagEnabled(snap[chave])
}

export function useConteudoFlag(chave: string) {
  const initial = enabledFromSnapshot(chave)
  const [enabled, setEnabled] = useState(initial ?? true)
  const [loading, setLoading] = useState(initial === null)
  const hasLoaded = useRef(initial !== null)

  const load = useCallback(
    (silent = false) => {
      if (!silent) setLoading(true)
      return getConteudoChave(chave)
        .then((v) => setEnabled(isConteudoFlagEnabled(v)))
        .catch(() => {
          if (!hasLoaded.current) setEnabled(true)
        })
        .finally(() => {
          hasLoaded.current = true
          setLoading(false)
        })
    },
    [chave]
  )

  useEffect(() => {
    let cancelled = false
    const run = () => {
      if (cancelled) return
      const fromSnap = enabledFromSnapshot(chave)
      if (fromSnap !== null) setEnabled(fromSnap)
      void load(hasLoaded.current)
    }
    run()
    const unsub = subscribeConteudoUpdated(run)
    return () => {
      cancelled = true
      unsub()
    }
  }, [chave, load])

  return { enabled, loading }
}

/** Redireciona para `/menu` quando a funcionalidade está desativada. */
export function useRequireConteudoFlag(chave: string, redirectTo = '/menu') {
  const navigate = useNavigate()
  const { enabled, loading } = useConteudoFlag(chave)

  useEffect(() => {
    if (!loading && !enabled) navigate(redirectTo, { replace: true })
  }, [loading, enabled, navigate, redirectTo])

  return { enabled, loading }
}
